import { useAppSelector } from 'hooks/useAppSelector';
import { useTranslation } from 'hooks/useTranslation';
import { useCallback } from 'react';
import type { WorkspaceType } from 'state/workspaces/model';

import { DocumentLink } from '../design-system/DocumentLink';
import { LanguageLink } from '../design-system/LanguageLink';
import { getKidDocumentError, getKidDocuments, getKidDocumentTimeout, hasKidError } from '../selectors';
import { useOnDownloadDocument } from './useOnDownloadDocument';

type Props = {
  tabId: string;
  workspaceType: WorkspaceType;
  className?: string;
};

export function KidDocuments({ tabId, workspaceType, className }: Props) {
  const label = useTranslation('form.buttons.KidDocuments');
  const documents = useAppSelector(state => getKidDocuments(state, workspaceType, tabId));
  const rejections = useAppSelector(state => getKidDocumentError(state, workspaceType, tabId));
  const hasError = useAppSelector(state => hasKidError(state, workspaceType, tabId));
  const hasTimeout = useAppSelector(state => getKidDocumentTimeout(state, workspaceType, tabId));
  const { onDownload, isDownloading, onShowError } = useOnDownloadDocument({
    errorLabelKey: 'errorDetails.kidDocument.standardMessage',
    event: 'Download KID language',
  });

  const onError = useCallback(() => {
    if (hasTimeout) {
      onShowError([]);
    } else {
      onShowError(rejections ?? []);
    }
  }, [hasTimeout, onShowError, rejections]);

  if (hasTimeout || hasError) {
    return (
      <DocumentLink label={label} className={className}>
        <LanguageLink
          langCode={null}
          status="error"
          severity={hasTimeout ? 'warning' : 'danger'}
          onShowError={onError}
        />
      </DocumentLink>
    );
  }

  if (documents.length === 0) {
    return null;
  }

  return (
    <DocumentLink label={label} className={className}>
      {documents.map((doc, i) => {
        if (doc.documentToken !== undefined && isDownloading) {
          return <LanguageLink key={i} langCode={doc.language} status="pending" />;
        }
        return <LanguageLink key={i} langCode={doc.language} status="success" onDownload={() => onDownload(doc)} />;
      })}
    </DocumentLink>
  );
}
